import React, { useState, useEffect, useRef } from 'react';
import Popup from '../Popup2';
import { updateDocumentInSubcollection } from '../../firestore_sub';
import { auth } from '../../firebase';
import { updateDocument, getDocument } from '../../firestore';

const ROWS = 25;
const COLS = 50;
const INITIAL_SNAKE = [{ row: 12, col: 12 }];
const INITIAL_DIRECTION = 'RIGHT';

const generateFood = () => {
  return {
    row: Math.floor(Math.random() * ROWS),
    col: Math.floor(Math.random() * COLS),
  };
};

const SnakeGame = () => {
  const [snake, setSnake] = useState(INITIAL_SNAKE);
  const [direction, setDirection] = useState(INITIAL_DIRECTION);
  const [food, setFood] = useState(generateFood());
  const [gameOver, setGameOver] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [showPopup, setShowPopup] = useState(false);
  const [score, setScore] = useState(0);
  const [lives, setLives] = useState(3);
  const gameOverRef = useRef(false);
  const uid = auth.currentUser ? auth.currentUser.uid : null;

  // Timer to show popup at regular intervals
  useEffect(() => {
    if (!gameOverRef.current && lives > 0 && !showPopup) {
      const popupTimer = setTimeout(() => {
        setShowPopup(true);
        setIsPaused(true);
      }, 15000); // 15 seconds between popups

      return () => clearTimeout(popupTimer);
    }
  }, [gameOverRef.current, lives, showPopup]);

  // Close popup and resume game
  const handleClosePopup = () => {
    setShowPopup(false);
    setIsPaused(false);
  };

  const handlePopupIncorrect = () => {
    console.log("Incorrect answer");
    setLives((prevLives) => {
      const updatedLives = prevLives;
      if (updatedLives <= 0) {
        setGameOver(true);
        return 0;
      }
      return updatedLives;
    });
  };

    useEffect(() => {
      if (lives <= 0) {
        setGameOver(true);
        setIsPaused(true);
      }
    }, [lives]);

  // Add event listener for keydown to prevent scrolling
  useEffect(() => {
    const preventArrowKeyScroll = (e) => {
      if (["ArrowUp", "ArrowDown", "ArrowLeft", "ArrowRight"].includes(e.key)) {
        e.preventDefault();
      }
    };
    window.addEventListener("keydown", preventArrowKeyScroll);
    return () => {
      window.removeEventListener("keydown", preventArrowKeyScroll);
    };
  }, []);

  const checkCollision = (snake) => {
    const head = snake[0];
    return (
      snake.slice(1).some((segment) => segment.row === head.row && segment.col === head.col) ||
      head.row < 0 ||
      head.row >= ROWS ||
      head.col < 0 ||
      head.col >= COLS
    );
  };

  const resetGame = () => {
    setSnake(INITIAL_SNAKE);
    setDirection(INITIAL_DIRECTION);
    setFood(generateFood());
    setGameOver(false);
    gameOverRef.current = false;
    setScore(0);
    setLives(3);
  };

  useEffect(() => {
    if (!gameOver && !isPaused) {
      const moveSnake = () => {
        const newSnake = snake.map((segment) => ({ ...segment }));
        const head = { ...newSnake[0] };

        switch (direction) {
          case 'UP':
            head.row = (head.row - 1 + ROWS) % ROWS;
            break;
          case 'DOWN':
            head.row = (head.row + 1) % ROWS;
            break;
          case 'LEFT':
            head.col = (head.col - 1 + COLS) % COLS;
            break;
          case 'RIGHT':
            head.col = (head.col + 1) % COLS;
            break;
          default:
            break;
        }

        newSnake.unshift(head);

        if (head.row === food.row && head.col === food.col) {
          setFood(generateFood());
          setScore(score + 10);
        } else {
          newSnake.pop();
        }

        if (checkCollision(newSnake)) {
          if (lives > 1) {
            setLives((prevLives) => prevLives - 1);
            setIsPaused(true);
          } else {
            gameOverDBUpdate();
            setGameOver(true);
            gameOverRef.current = true;
          }
        } else {
          setSnake(newSnake);
        }
      };

      const gameInterval = setInterval(moveSnake, 100);
      return () => clearInterval(gameInterval);
    }
  }, [snake, direction, food, gameOver, isPaused, score, lives]);

  // Key press handler
  useEffect(() => {
    const handleKeyPress = (e) => {
      if (!isPaused) {
        switch (e.key) {
          case 'ArrowUp':
            if (direction !== 'DOWN') setDirection('UP');
            break;
          case 'ArrowDown':
            if (direction !== 'UP') setDirection('DOWN');
            break;
          case 'ArrowLeft':
            if (direction !== 'RIGHT') setDirection('LEFT');
            break;
          case 'ArrowRight':
            if (direction !== 'LEFT') setDirection('RIGHT');
            break;
          default:
            break;
        }
      }
    };

    document.addEventListener('keydown', handleKeyPress);
    return () => document.removeEventListener('keydown', handleKeyPress);
  }, [direction, isPaused]);

  const renderHearts = () => {
    return Array.from({ length: 3 }).map((_, index) => (
      <span
        key={index}
        className={`text-red-500 text-2xl ${index < lives ? "opacity-100" : "opacity-30"}`}
      >
        ❤️
      </span>
    ));
  };

  const exitGame = () => {
    window.location.href = "/games";
  };

  // Game over DB update functions (same as before)
  async function gameOverDBUpdate() {
    const queryParams = new URLSearchParams(window.location.search);
    var gameId = queryParams.get("id");
    const userId = auth.currentUser.uid;
    const subCollectionPath = `gameplay/${userId}/gameplays/`;
    let currentScore = score;
    const gameplayData = {
      lives: lives,
      score: currentScore,
    };
    await updateDocument(subCollectionPath, gameId, gameplayData);
    await totalGameDataUpdate(currentScore);
  }

  async function totalGameDataUpdate(score) {
    const subCollectionPath = `gameplay`;
    const userId = auth.currentUser.uid;
    var docData = await getDocument(subCollectionPath, userId);
    let oldScore = docData.score;
    let total_games = docData.games_played + 1;
    let currentScore = score + oldScore;
    const gameplayData = {
      games_played: total_games,
      score: currentScore,
    };
    await updateDocument(subCollectionPath, userId, gameplayData);
  }

  return (
    <div className="min-h-screen bg-[var(--background-color)] py-8 px-4 sm:px-6 lg:px-8 animate-fadeIn">
      <div className="max-w-7xl mx-auto">
        <h1 className="font-['Jersey_25',sans-serif] text-4xl md:text-5xl font-bold text-center mb-10 text-yellow-500 bg-gradient-to-r from-[var(--primary-color)] via-[var(--interactive-color)] to-[var(--accent-color)] text-transparent bg-clip-text transform transition-all">
          Snake Game
        </h1>
  
        <div className="bg-[var(--surface-color)] rounded-2xl shadow-lg overflow-hidden border border-[var(--border-color)]">
          <div className="bg-[var(--primary-color)] py-4 px-6">
            <div className="flex items-center justify-between">
              <div className="text-white text-3xl flex items-center gap-6">
                <h1 className="text-2xl font-bold text-white">Score: {score}</h1>
                <h1 className="text-2xl font-bold text-white">
                  Lives: {renderHearts()}
                </h1>
              </div>
  
              <div className="flex items-center gap-4">
                <button
                  className="group relative inline-flex items-center px-6 py-3 bg-[var(--interactive-color)] text-white rounded-lg text-xl font-bold transform transition-all duration-300 hover:bg-opacity-90 hover:scale-105 focus:outline-none focus:ring-2 focus:ring-[var(--interactive-color)] focus:ring-offset-2 focus:ring-offset-[var(--background-color)]"
                  onClick={resetGame}
                >
                  Restart
                </button>
                <button
                    className="group relative inline-flex items-center px-6 py-3 bg-[var(--error-color)] text-white rounded-lg text-xl font-bold transform transition-all duration-300 hover:bg-opacity-90 hover:scale-105 focus:outline-none focus:ring-2 focus:ring-[var(--error-color)] focus:ring-offset-2 focus:ring-offset-[var(--background-color)]"
                    onClick={exitGame}
                  >
                    Exit
                  </button>
              </div>
            </div>
          </div>
  
          <div className="p-6">
            <div
              className="bg-black rounded-2xl shadow-lg overflow-hidden border border-[var(--border-color)]"
              style={{
                display: 'grid',
                gridTemplateRows: `repeat(${ROWS}, 1fr)`,
                gridTemplateColumns: `repeat(${COLS}, 1fr)`,
                aspectRatio: '1/0.5',
              }}
            >
              {Array.from({ length: ROWS }).map((_, rowIndex) =>
                Array.from({ length: COLS }).map((_, colIndex) => (
                  <div
                    key={`${rowIndex}-${colIndex}`}
                    className={`${
                      snake.some(
                        (segment) =>
                          segment.row === rowIndex && segment.col === colIndex
                      )
                        ? 'bg-yellow-500 rounded'
                        : food.row === rowIndex && food.col === colIndex
                        ? 'bg-red-500 rounded-2xl'
                        : 'bg-transparent'
                    }`}
                  ></div>
                )))
              }
            </div>
          </div>
        </div>
  
        {gameOver && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center">
            <div className="bg-[var(--surface-color)] rounded-2xl shadow-lg p-8 text-center">
              <p className="text-4xl font-bold text-white mb-6">
                Game Over! <b>Your Score 🐍 {score}</b>
              </p>
              <div className="flex gap-4 justify-center">
                <button
                  className="group relative inline-flex items-center px-6 py-3 bg-[var(--interactive-color)] text-white rounded-lg text-xl font-bold transform transition-all duration-300 hover:bg-opacity-90 hover:scale-105 focus:outline-none focus:ring-2 focus:ring-[var(--interactive-color)] focus:ring-offset-2 focus:ring-offset-[var(--background-color)]"
                  onClick={resetGame}
                >
                  Restart
                </button>
                <button
                  className="group relative inline-flex items-center px-6 py-3 bg-[var(--error-color)] text-white rounded-lg text-xl font-bold transform transition-all duration-300 hover:bg-opacity-90 hover:scale-105 focus:outline-none focus:ring-2 focus:ring-[var(--error-color)] focus:ring-offset-2 focus:ring-offset-[var(--background-color)]"
                  onClick={exitGame}
                >
                  Exit
                </button>
              </div>
            </div>
          </div>
        )}
  
        {showPopup && (
          <Popup
            onClose={handleClosePopup}
            onIncorrectAnswer={handlePopupIncorrect}
            score={score}
            setScore={setScore}
            lives={lives}
            setLives={setLives}
            useLives={true}
            gameOver={gameOver || gameOverRef.current}
            setIsPause={setIsPaused}
          />
        )}
      </div>
    </div>
  );
};

export default SnakeGame;